// Stripe billing helpers. We use Stripe-hosted Checkout (no Stripe.js): the
// backend returns a session/portal URL and we redirect the browser to it.
// Reuses the project's inline auth pattern: getToken() from Clerk + VITE_API_URL.

type GetToken = () => Promise<string | null>

const apiBase = import.meta.env.VITE_API_URL || '/api/v1'

export type PlanId = 'solo' | 'starter' | 'growth'

async function authedPost(getToken: GetToken, path: string, body: unknown): Promise<any> {
  const token = await getToken()
  if (!token) throw new Error('Not authenticated')

  const res = await fetch(`${apiBase}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  })

  if (!res.ok) {
    // Surface the backend's error message when available
    let message = `Request failed (${res.status})`
    try {
      const err = await res.json()
      message = err.message || err.error || message
    } catch {
      /* non-JSON error body */
    }
    throw new Error(message)
  }

  return res.json()
}

/** Create a Checkout Session for the given plan and redirect to Stripe. */
export async function startCheckout(getToken: GetToken, planId: PlanId): Promise<void> {
  const { sessionUrl } = await authedPost(getToken, '/checkout/create-session', { planId })
  if (!sessionUrl) throw new Error('No checkout URL returned')
  window.location.href = sessionUrl
}

/** Create a one-time Checkout Session for the backlog pass and redirect. */
export async function startBacklogCheckout(getToken: GetToken): Promise<void> {
  const { sessionUrl } = await authedPost(getToken, '/checkout/create-backlog-session', {})
  if (!sessionUrl) throw new Error('No checkout URL returned')
  window.location.href = sessionUrl
}

/** Open the Stripe Customer Portal to manage/cancel billing. */
export async function openBillingPortal(getToken: GetToken): Promise<void> {
  const data = await authedPost(getToken, '/subscription/portal', {})
  const url = data.portalUrl || data.url
  if (!url) throw new Error('No billing portal URL returned')
  window.location.href = url
}
